/**
 * Fetch a cross-origin image as a data URL, once per URL per page load.
 *
 * html2canvas cannot draw a remote image into a canvas it must then export, so
 * the share receipt inlines the skin logo as base64 first. That fetch needs the
 * remote host to answer with `Access-Control-Allow-Origin`.
 *
 * The S3 bucket serving `skins/logos/platform_*.png` currently does NOT send
 * that header — it answers 200 with no ACAO, so the browser blocks the read and
 * the receipt renders without a logo. That is a BUCKET CORS CONFIGURATION issue
 * and cannot be fixed from here; the only front-end options are to inline it
 * (this) or proxy it through our own API.
 *
 * What this module does fix is the retrying: the call used to run from a
 * component watcher, so every re-creation of the receipt fired another request
 * that was certain to fail the same way. Results are memoised per URL —
 * successes and failures alike — so a broken host is asked exactly once.
 */

/** url -> Promise<string|null>. A settled rejection is never retried. */
const cache = new Map();

/** Reset point for tests; not used by the app. */
export function _clearImageDataUrlCache() {
    cache.clear();
}

async function fetchAsDataUrl(url) {
    // Cache-buster: an entry cached from a plain <img> load carries no CORS
    // headers, and reusing it would fail even against a correctly configured
    // host.
    const bustUrl = url + (url.includes('?') ? '&' : '?') + '_t=' + Date.now();

    const res = await fetch(bustUrl, { mode: 'cors', cache: 'no-cache' });
    if (!res.ok) {
        throw new Error('HTTP ' + res.status);
    }

    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error('FileReader failed'));
        reader.readAsDataURL(blob);
    });
}

/**
 * Resolve to a base64 data URL, or to null when the image cannot be read.
 *
 * Never rejects: callers render without the image rather than breaking. The
 * failure is reported once per URL instead of being swallowed, because a
 * missing ACAO header is a server-side fix nobody makes if it stays invisible.
 */
export function toDataUrl(url) {
    if (!url) return Promise.resolve(null);

    if (!cache.has(url)) {
        cache.set(url, fetchAsDataUrl(url).catch((err) => {
            console.warn(
                '[imageDataUrl] could not inline ' + url + ' — the receipt will ' +
                'render without it. If this is a CORS block, the host must send ' +
                'Access-Control-Allow-Origin.',
                err && err.message
            );
            return null;
        }));
    }

    return cache.get(url);
}
